import {
  CloseButton,
  Combobox,
  InputBase,
  Loader,
  ScrollArea,
  Text,
  useCombobox,
} from '@mantine/core';
import React, {useEffect, useState} from 'react';
import type {ISelectOption} from 'src/types/Form';

/**
 * Searchable single-select combobox with a clear button, backed by Mantine
 * `Combobox`/`InputBase`.
 * @registry Type-to-search single-select with clear affordance.
 *
 * Ported from `library/elements/Autocomplete`. Prop surface kept identical.
 */
export interface AutocompleteProps {
  label?: string;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
  options: ISelectOption[];
  value: ISelectOption | null;
  disabled?: boolean;
  loading?: boolean;
  onSelectChangeCallback: (value: ISelectOption | null) => void;
  onInputChange?: (event: React.SyntheticEvent, newInputValue: string) => void;
}
export const Autocomplete: React.FC<AutocompleteProps> = React.memo(
  ({
    label,
    placeholder,
    error,
    required,
    options,
    value,
    disabled,
    loading,
    onSelectChangeCallback,
    onInputChange,
  }) => {
    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState('');

    // Update search when value changes externally
    useEffect(() => {
      setSearch(value?.label || '');
    }, [value]);

    const handleOptionSubmit = (val: string) => {
      const selectedOption =
        options.find(option => option.label === val) || null;
      onSelectChangeCallback(selectedOption);
      setSearch(val);
      combobox.closeDropdown();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      setSearch(newValue);
      combobox.openDropdown();

      // Call the onInputChange callback if provided
      if (onInputChange) {
        onInputChange(event, newValue);
      }
    };

    const handleInputBlur = () => {
      combobox.closeDropdown();
      setSearch(value?.label || '');
    };

    const handleClear = () => {
      onSelectChangeCallback(null);
      setSearch('');
      combobox.closeDropdown();
    };

    const optionsElements = options.map(option => (
      <Combobox.Option value={option.label} key={option.id}>
        <Text size="xs">{option.label}</Text>
      </Combobox.Option>
    ));

    return (
      <Combobox
        store={combobox}
        withinPortal={true}
        zIndex={1001}
        onOptionSubmit={handleOptionSubmit}>
        <Combobox.Target>
          <InputBase
            label={label}
            withAsterisk={required}
            error={error}
            disabled={disabled}
            rightSection={
              loading ? (
                <Loader size="xs" />
              ) : value ? (
                <CloseButton
                  size="sm"
                  onMouseDown={event => event.preventDefault()}
                  onClick={handleClear}
                  aria-label="Clear value"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            value={search}
            onChange={handleInputChange}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={handleInputBlur}
            placeholder={placeholder ?? (label ? `Type to search ${label.toLowerCase()}` : 'Type to search')}
            rightSectionPointerEvents={value ? 'all' : 'none'}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize mah={350} type="scroll">
              {optionsElements.length > 0 ? (
                optionsElements
              ) : (
                <Combobox.Empty>Nothing found</Combobox.Empty>
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    );
  }
);
